/**
 * Parser for the free-text report a note-push child run writes into the
 * ``run_computer_use_task`` tool result on the parent timeline.
 *
 * The report is prose with a handful of machine-readable grammar lines mixed
 * in. We pull the grammar out and ignore everything else. The grammar:
 *
 *   PATIENT: verified <evidence>          (first report line — identity check)
 *   PATIENT: mismatch — aborted
 *   PATIENT: unverified — aborted
 *   FIELD: <label> -> <selector json> [empty|filled]   (read-only discovery)
 *   PLACED: <section heading> -> <selector json>[, <selector json>...]
 *   FAILED: <section heading> -> <reason>
 *
 * ``<selector json>`` is opaque ``AxSelector`` wire JSON (``{role?, name?,
 * matchMode}``). We keep every selector as a raw string — never parse or
 * re-validate its internals. A selector object contains commas, so the
 * multi-selector PLACED list is split on *top-level* commas only (see
 * {@link splitTopLevelSelectors}); a naive ``.split(',')`` would shred the JSON.
 */

export type NotePushPatientStatus = 'verified' | 'mismatch' | 'unverified';

export interface NotePushReportPatient {
  status: NotePushPatientStatus;
  /**
   * Free-text evidence, present only for the ``verified`` variant. May contain
   * patient identifiers (PHI) — render-only, never log/serialise.
   */
  evidence?: string;
}

export interface NotePushReportField {
  label: string;
  /** Raw ``AxSelector`` wire JSON — kept verbatim, never parsed. */
  selector: string;
  state?: 'empty' | 'filled';
}

export interface NotePushReportPlaced {
  sectionHeading: string;
  /** One or more raw selector strings the section was placed against. */
  selectors: string[];
}

export interface NotePushReportFailed {
  sectionHeading: string;
  reason: string;
}

export interface NotePushReport {
  patient?: NotePushReportPatient;
  /** Every ``FIELD:`` line, in report order — regardless of identity. */
  fields: NotePushReportField[];
  /**
   * The subset of {@link fields} that appear *after* a ``PATIENT: verified``
   * line. Grammar order matters: the identity check is the first report line,
   * so a ``FIELD:`` before it (or before a ``mismatch`` / ``unverified``
   * patient) may have been read from the wrong chart and is excluded here.
   * This is the only field set safe to surface as mapping candidates.
   */
  verifiedFields: NotePushReportField[];
  placed: NotePushReportPlaced[];
  failed: NotePushReportFailed[];
}

const ARROW = '->';

const PATIENT_RE = /^PATIENT:\s*(verified|mismatch|unverified)\b(.*)$/i;
const FIELD_STATE_RE = /\[(empty|filled)\]\s*$/i;

/**
 * Parse a child-run report into typed grammar structures. Tolerant of
 * surrounding prose — non-grammar lines are dropped. Returns empty
 * collections (and no ``patient``) when nothing matches.
 */
export function parseNotePushReport(text: string | undefined | null): NotePushReport {
  const report: NotePushReport = {
    fields: [],
    verifiedFields: [],
    placed: [],
    failed: [],
  };
  if (!text) return report;

  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;

    const patient = matchPatient(line);
    if (patient) {
      // The PATIENT line is the first report line; if a later line also
      // matches (e.g. echoed in prose), the first one wins.
      if (!report.patient) report.patient = patient;
      continue;
    }

    const field = matchField(line);
    if (field) {
      report.fields.push(field);
      // Only fields seen after a verified identity are safe candidates. The
      // first PATIENT line wins (above), so this reads the settled status.
      if (report.patient?.status === 'verified') report.verifiedFields.push(field);
      continue;
    }

    const placed = matchPlaced(line);
    if (placed) {
      report.placed.push(placed);
      continue;
    }

    const failed = matchFailed(line);
    if (failed) {
      report.failed.push(failed);
      continue;
    }
    // Prose — ignore.
  }

  return report;
}

function matchPatient(line: string): NotePushReportPatient | null {
  const match = line.match(PATIENT_RE);
  if (!match?.[1]) return null;
  const status = match[1].toLowerCase() as NotePushPatientStatus;
  if (status === 'verified') {
    const evidence = (match[2] ?? '').trim();
    return evidence ? { status, evidence } : { status };
  }
  return { status };
}

/**
 * Split a ``<heading/label> -> <rest>`` line on the first ``->``. The heading
 * side is trimmed; the ``after`` side is returned raw (each caller trims or
 * parses it as its grammar needs). ``null`` when there is no arrow.
 */
function splitOnArrow(rest: string): { before: string; after: string } | null {
  const arrowIndex = rest.indexOf(ARROW);
  if (arrowIndex === -1) return null;
  return {
    before: rest.slice(0, arrowIndex).trim(),
    after: rest.slice(arrowIndex + ARROW.length),
  };
}

function matchField(line: string): NotePushReportField | null {
  const match = line.match(/^FIELD:\s*(.*)$/i);
  if (match?.[1] === undefined) return null;

  let rest = match[1].trim();
  let state: 'empty' | 'filled' | undefined;
  const stateMatch = rest.match(FIELD_STATE_RE);
  if (stateMatch?.[1] !== undefined && stateMatch.index !== undefined) {
    state = stateMatch[1].toLowerCase() as 'empty' | 'filled';
    rest = rest.slice(0, stateMatch.index).trimEnd();
  }

  const split = splitOnArrow(rest);
  if (!split) return null;
  const label = split.before;
  const selector = split.after.trim();
  if (!label || !selector) return null;

  return state ? { label, selector, state } : { label, selector };
}

function matchPlaced(line: string): NotePushReportPlaced | null {
  const match = line.match(/^PLACED:\s*(.*)$/i);
  if (match?.[1] === undefined) return null;

  const split = splitOnArrow(match[1]);
  if (!split) return null;
  const sectionHeading = split.before;
  const selectors = splitTopLevelSelectors(split.after);
  if (!sectionHeading || selectors.length === 0) return null;

  return { sectionHeading, selectors };
}

function matchFailed(line: string): NotePushReportFailed | null {
  const match = line.match(/^FAILED:\s*(.*)$/i);
  if (match?.[1] === undefined) return null;

  const split = splitOnArrow(match[1]);
  if (!split) return null;
  const sectionHeading = split.before;
  const reason = split.after.trim();
  if (!sectionHeading || !reason) return null;

  return { sectionHeading, reason };
}

/**
 * Split a PLACED selector list on top-level commas — commas at brace/bracket
 * depth 0 and outside string literals. The selector JSON (``{"role":"textbox",
 * "name":"History","matchMode":"exact"}``) is full of commas, so a naive split
 * would tear each object apart. Selectors are returned as raw trimmed strings.
 */
function splitTopLevelSelectors(input: string): string[] {
  const parts: string[] = [];
  let current = '';
  let depth = 0;
  let inString = false;
  let quote = '';

  for (let index = 0; index < input.length; index += 1) {
    const char = input[index];

    if (inString) {
      current += char;
      if (char === '\\') {
        // Keep the escaped character verbatim so an escaped quote can't end
        // the string early.
        index += 1;
        if (index < input.length) current += input[index];
        continue;
      }
      if (char === quote) inString = false;
      continue;
    }

    if (char === '"' || char === "'") {
      inString = true;
      quote = char;
      current += char;
      continue;
    }
    if (char === '{' || char === '[') {
      depth += 1;
      current += char;
      continue;
    }
    if (char === '}' || char === ']') {
      depth = Math.max(0, depth - 1);
      current += char;
      continue;
    }
    if (char === ',' && depth === 0) {
      const trimmed = current.trim();
      if (trimmed) parts.push(trimmed);
      current = '';
      continue;
    }
    current += char;
  }

  const last = current.trim();
  if (last) parts.push(last);
  return parts;
}
